import { formatarDataCurta } from '@/pages/gerenciar/utils';
import type { PessoaImpressao, RespostaImpressao } from '../types';

interface Props {
  pessoa: PessoaImpressao;
  eventoNome: string;
  eventoData: string;
  impressoEm: string;
}

function ordenar(respostas: RespostaImpressao[]): { secao: string; itens: RespostaImpressao[] }[] {
  const mapa = new Map<string, RespostaImpressao[]>();
  [...respostas]
    .sort((a, b) => a.ordem - b.ordem)
    .forEach((r) => {
      const secao = (r.secao || 'Ficha').trim() || 'Ficha';
      if (!mapa.has(secao)) mapa.set(secao, []);
      mapa.get(secao)!.push(r);
    });
  return [...mapa.entries()].map(([secao, itens]) => ({ secao, itens }));
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string | null }) {
  return (
    <div className="min-w-0">
      <p className="text-[8pt] uppercase tracking-wide text-secondary-500">{rotulo}</p>
      <p className="break-words text-[10pt] font-medium text-foreground-950">{valor || '—'}</p>
    </div>
  );
}

// Uma ficha A4 por pessoa. As classes abaixo são controladas pelo CSS de impressão da página.
export default function FichaImpressao({ pessoa, eventoNome, eventoData, impressoEm }: Props) {
  const secoes = ordenar(pessoa.respostas);

  return (
    <article className="ficha-pagina flex flex-col rounded-lg border border-secondary-200 bg-white p-8 text-foreground-950">
      <header className="flex items-start justify-between gap-4 border-b border-secondary-200 pb-3">
        <div className="min-w-0">
          <p className="text-[9pt] font-semibold uppercase tracking-wide text-primary-700">
            {eventoNome}
          </p>
          <h1 className="mt-0.5 text-[16pt] font-bold leading-tight">{pessoa.nome}</h1>
          <p className="mt-0.5 text-[9.5pt] text-secondary-600">
            {pessoa.modalidade}
            {pessoa.grupo ? ` · ${pessoa.grupo}` : ''}
          </p>
        </div>
        <div className="shrink-0 text-right text-[9pt] text-secondary-600">
          <p>{eventoData}</p>
          <p className="mt-0.5 font-semibold text-foreground-900">Ficha de inscrição</p>
        </div>
      </header>

      <div className="mt-4 flex gap-5">
        {pessoa.fotoUrl ? (
          <img
            src={pessoa.fotoUrl}
            alt={`Foto de ${pessoa.nome}`}
            className="h-[32mm] w-[26mm] shrink-0 rounded border border-secondary-300 object-contain object-center"
          />
        ) : (
          <div className="flex h-[32mm] w-[26mm] shrink-0 items-center justify-center rounded border border-dashed border-secondary-300 text-[8pt] text-secondary-400">
            Sem foto
          </div>
        )}

        <div className="grid min-w-0 flex-1 grid-cols-3 gap-x-4 gap-y-2">
          <Linha rotulo="Situação" valor={pessoa.situacao} />
          <Linha rotulo="Senha" valor={pessoa.senha} />
          <Linha
            rotulo="Idade no dia do evento"
            valor={pessoa.idade !== null ? `${pessoa.idade} anos` : null}
          />
          <Linha rotulo="Telefone" valor={pessoa.telefone} />
          <Linha rotulo="CPF" valor={pessoa.cpf} />
          <Linha
            rotulo="Nascimento"
            valor={pessoa.nascimento ? formatarDataCurta(pessoa.nascimento) : null}
          />
          <Linha rotulo="Contato" valor={pessoa.contatoNome} />
          <Linha rotulo="Telefone do contato" valor={pessoa.contatoTelefone} />
          <Linha rotulo="E-mail" valor={pessoa.email} />
        </div>
      </div>

      <div className="mt-5 flex-1 space-y-4">
        {secoes.length === 0 ? (
          <p className="text-[10pt] text-secondary-500">Sem respostas registradas.</p>
        ) : (
          secoes.map((s) => (
            <section key={s.secao} className="break-inside-avoid">
              <h2 className="border-b border-secondary-200 pb-1 text-[10pt] font-bold uppercase tracking-wide text-foreground-800">
                {s.secao}
              </h2>
              <div className="mt-1.5 grid grid-cols-2 gap-x-5 gap-y-2">
                {s.itens.map((r, i) => (
                  <div key={`${r.ordem}-${i}`} className="min-w-0">
                    <p className="text-[8pt] text-secondary-500">{r.enunciado}</p>
                    {r.fotoUrl ? (
                      <img
                        src={r.fotoUrl}
                        alt={r.enunciado}
                        className="mt-1 h-[24mm] w-[20mm] rounded border border-secondary-300 object-contain object-center"
                      />
                    ) : (
                      <p className="break-words text-[10pt] text-foreground-900">
                        {r.resposta || '—'}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))
        )}
      </div>

      <footer className="mt-auto border-t border-secondary-200 pt-2 text-[8pt] text-secondary-500">
        Impresso em {impressoEm}
      </footer>
    </article>
  );
}